import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/test-system.css';
import './TestCertificates.css';
import Navigation from '../components/Navigation';
import TestNav from '../components/tests/TestNav';
import { getMyCertificates, readApiError, setCertificateSharing } from '../api/testSystem';
import { isAuthenticated } from '../utils/auth';
import { formatToLocalDate } from '../utils/dateUtils';
import {
    certificateKindLabel,
    formatScore,
    getPublicCertificateUrl,
    hasHonours,
    isTranscript,
    orderCertificates,
    showsScore,
    transcriptItems,
} from '../utils/certificates';

const AlertIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);

const DownloadIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
);

const CertificateCard = ({ certificate, onToggleShare, pending, error, copied, onCopy }) => {
    const active = certificate.status === 'active';
    const hasScore = showsScore(certificate);
    const honours = hasHonours(certificate);
    const kindLabel = certificateKindLabel(certificate);
    const publicUrl = getPublicCertificateUrl(certificate.certificate_id);
    const transcript = isTranscript(certificate);
    const documentWord = transcript ? 'transcript' : 'certificate';
    const items = transcriptItems(certificate);

    return (
        <article
            className={`ts-panel ts-cert${transcript ? ' ts-cert--transcript' : ''}`}
            data-testid="certificate-card"
            data-kind={transcript ? 'transcript' : 'certificate'}
        >
            <div className="ts-cert-preview">
                {certificate.preview_url ? (
                    <img
                        src={certificate.preview_url}
                        alt={`${certificate.structure_name} ${documentWord}`}
                        loading="lazy"
                    />
                ) : (
                    <div className="ts-cert-preview-empty" data-testid="certificate-no-preview">
                        {active ? 'Preview unavailable' : 'Revoked'}
                    </div>
                )}
            </div>

            <div className="ts-cert-body">
                <div className="ts-cert-head">
                    <div>
                        <span className="ts-cert-kind" data-testid="certificate-kind">{kindLabel}</span>
                        <h2 className="ts-cert-title">{certificate.structure_name || 'Certificate'}</h2>
                    </div>
                    {/* A certificate is either valid or withdrawn, and a card on "my
                        certificates" is the valid case. Only the exception needs saying —
                        a green "Active" badge on every row is a status enum wearing a
                        chip, not information. */}
                    {(honours || !active) && (
                        <div className="ts-cert-chips">
                            {honours && (
                                <span className="ts-chip ts-chip--live" data-testid="certificate-honours">
                                    With Honours
                                </span>
                            )}
                            {!active && (
                                <span className="ts-chip ts-chip--warn" data-testid="certificate-status">
                                    No longer valid
                                </span>
                            )}
                        </div>
                    )}
                </div>

                <dl className="ts-facts ts-cert-facts">
                    {/* A participation certificate says the trial was completed, not how
                        well — only a transcript carries a score. */}
                    {hasScore && (
                        <div className="ts-fact" data-testid="certificate-score">
                            <dt>Overall score</dt>
                            <dd>
                                {formatScore(certificate.score)}
                                <span className="ts-cert-scale"> of 100</span>
                            </dd>
                        </div>
                    )}
                    <div className="ts-fact">
                        <dt>Issued</dt>
                        <dd>{formatToLocalDate(certificate.issued_at)}</dd>
                    </div>
                </dl>

                {items.length > 0 && (
                    <ul className="ts-cert-items" data-testid="transcript-items">
                        {items.map((item, index) => (
                            <li
                                key={`${index}-${item.name}`}
                                className={`ts-cert-item${item.score === null ? ' ts-cert-item--na' : ''}`}
                                data-testid="transcript-item"
                            >
                                <div className="ts-cert-item-text">
                                    <span className="ts-cert-item-name">{item.name}</span>
                                    {item.definition && (
                                        <span className="ts-cert-item-definition">{item.definition}</span>
                                    )}
                                    {item.score === null && (
                                        <span className="ts-cert-item-na">Not assessed on this programme</span>
                                    )}
                                </div>
                                <span className="ts-cert-item-score" data-testid="transcript-item-score">
                                    {formatScore(item.score)}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}

                {!active && (
                    <p className="ts-note ts-note--warn">
                        <AlertIcon />
                        <span>
                            This {documentWord} was revoked. Its public page says so, and the files
                            are no longer served.
                        </span>
                    </p>
                )}

                {error && (
                    <p className="ts-note ts-note--danger" role="alert" data-testid="certificate-error">
                        <AlertIcon />
                        <span>{error}</span>
                    </p>
                )}

                <div className="ts-cert-share">
                    <button
                        type="button"
                        role="switch"
                        aria-checked={!!certificate.share_enabled}
                        className={`ts-switch${certificate.share_enabled ? ' ts-switch--on' : ''}`}
                        onClick={() => onToggleShare(certificate)}
                        disabled={pending}
                        data-testid={`share-toggle-${certificate.certificate_id}`}
                    >
                        <span className="ts-switch-track"><span className="ts-switch-thumb" /></span>
                        Public link {certificate.share_enabled ? 'on' : 'off'}
                    </button>

                    {transcript && (
                        <p className="ts-cert-share-note" data-testid="transcript-share-note">
                            The public link shows your scores.
                        </p>
                    )}

                    {certificate.share_enabled ? (
                        <div className="ts-cert-link" data-testid="certificate-public-link">
                            <code>{publicUrl}</code>
                            <button
                                className="ts-btn ts-btn--sm"
                                onClick={() => onCopy(certificate.certificate_id, publicUrl)}
                                data-testid={`copy-link-${certificate.certificate_id}`}
                            >
                                {copied === 'copied' ? 'Copied' : copied === 'failed' ? 'Copy failed' : 'Copy link'}
                            </button>
                        </div>
                    ) : (
                        <p className="ts-cert-share-off">
                            Anyone opening your link sees nothing while sharing is off.
                        </p>
                    )}
                </div>

                {certificate.pdf_url && (
                    <a
                        className="ts-btn ts-btn--primary ts-cert-download"
                        href={certificate.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`download-${certificate.certificate_id}`}
                    >
                        <DownloadIcon />
                        Download PDF
                    </a>
                )}
            </div>
        </article>
    );
};

const TestCertificates = () => {
    const navigate = useNavigate();

    const [certificates, setCertificates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [pendingId, setPendingId] = useState('');
    const [errors, setErrors] = useState({});
    const [copied, setCopied] = useState({});

    const loadCertificates = useCallback(async () => {
        setLoading(true);
        setLoadError('');

        try {
            setCertificates(await getMyCertificates());
        } catch (e) {
            setLoadError(readApiError(e, 'Your certificates could not be loaded.').message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated()) {
            navigate('/login');
            return;
        }

        loadCertificates();
    }, [navigate, loadCertificates]);

    const handleToggleShare = async (certificate) => {
        const { certificate_id: certificateId } = certificate;
        setPendingId(certificateId);
        setErrors(current => ({ ...current, [certificateId]: '' }));

        try {
            const updated = await setCertificateSharing(certificateId, !certificate.share_enabled);
            // The share response carries the facts but not the signed links, so it is
            // merged over the row rather than replacing it.
            setCertificates(current => current.map(row => (
                row.certificate_id === certificateId ? { ...row, ...(updated || {}) } : row
            )));
        } catch (e) {
            const { message } = readApiError(e, 'Sharing could not be updated.');
            setErrors(current => ({ ...current, [certificateId]: message }));
        }

        setPendingId('');
    };

    const handleCopy = async (certificateId, url) => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(current => ({ ...current, [certificateId]: 'copied' }));
        } catch (e) {
            // Clipboard access can be refused outright (permissions, insecure
            // context); the link is on screen, so it can still be copied by hand.
            console.error('Error copying the certificate link:', e);
            setCopied(current => ({ ...current, [certificateId]: 'failed' }));
        }
    };

    return (
        <>
            <Navigation />
            <div className="ts-page">
                <div className="ts-shell ts-shell--wide">
                    <TestNav />

                    <header className="ts-head">
                        <div className="ts-head-main">
                            <h1 className="ts-title">My certificates</h1>
                            <p className="ts-lede">
                                Certificates are issued once a cycle's analysis is complete. Each one
                                has a public page you can share with anyone — switch that link off at
                                any time and the page goes dark.
                            </p>
                        </div>
                    </header>

                    {loading ? (
                        <div className="ts-page-state ts-page-state--loading" data-testid="certificates-loading">
                            <div className="ts-spinner" />
                            <p>Loading your certificates…</p>
                        </div>
                    ) : loadError ? (
                        <div className="ts-page-state" role="alert">
                            <h2>Your certificates are unavailable</h2>
                            <p>{loadError}</p>
                            <button className="ts-btn" onClick={loadCertificates}>Try again</button>
                        </div>
                    ) : certificates.length === 0 ? (
                        <div className="ts-page-state" data-testid="certificates-empty">
                            <h2>No certificates yet</h2>
                            <p>
                                Take a test cycle through to the end and your certificate lands
                                here once it has been analysed.
                            </p>
                            <Link className="ts-btn" to="/tests">Browse the catalog</Link>
                        </div>
                    ) : (
                        <>
                            <div
                                className={`ts-cert-list${certificates.some(isTranscript) ? ' ts-cert-list--with-transcript' : ''}`}
                                data-testid="certificates-list"
                            >
                                {orderCertificates(certificates).map(certificate => (
                                    <CertificateCard
                                        key={certificate.certificate_id}
                                        certificate={certificate}
                                        onToggleShare={handleToggleShare}
                                        pending={pendingId === certificate.certificate_id}
                                        error={errors[certificate.certificate_id]}
                                        copied={copied[certificate.certificate_id]}
                                        onCopy={handleCopy}
                                    />
                                ))}
                            </div>
                            <p className="ts-cert-note">
                                Download links are signed and short-lived. If one stops working,
                                reload this page for a fresh one.
                            </p>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default TestCertificates;
